import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, XCircle, RefreshCw, AlertTriangle, ArrowRight,
  FileText, Users, Building2, ChevronRight, Loader, ExternalLink,
  Download, Search, X, Clock, Trash2
} from 'lucide-react';
import api from '../../services/api';

const fmtMoney = (n) => {
  const abs = Math.abs(n || 0);
  const sign = (n || 0) < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${(abs || 0).toFixed(2)}`;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    AUTHORISED: 'bg-blue-100 text-blue-700',
    PAID:       'bg-emerald-100 text-emerald-700',
    DRAFT:      'bg-slate-100 text-slate-500',
    VOIDED:     'bg-red-100 text-red-400',
    DELETED:    'bg-red-100 text-red-400',
    OVERDUE:    'bg-red-100 text-red-700 font-semibold',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${map[status] || 'bg-slate-100 text-slate-500'}`}>
      {status}
    </span>
  );
}

// ── Connection Card ───────────────────────────────────────────────────────────
function ConnectionCard({ status, onConnect, onDisconnect, onMigrate, migrating }) {
  if (!status) return (
    <div className="h-32 bg-white border border-slate-200 rounded-lg animate-pulse" />
  );

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="xero-connection-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-2.5 h-2.5 rounded-full ${status.connected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            <p className="text-sm font-semibold text-slate-800">
              {status.connected ? `Connected to ${status.tenant_name || 'Xero'}` : 'Not connected to Xero'}
            </p>
          </div>
          <p className="text-xs text-slate-400 ml-4">
            {status.connected
              ? `Token ${status.token_valid ? 'valid' : 'expired'} · ${(status.counts?.invoices || 0).toLocaleString()} invoices imported`
              : 'Click "Connect to Xero" to begin the one-time migration'}
          </p>
        </div>
        <div className="flex gap-2">
          {status.connected ? (
            <>
              <button
                onClick={onMigrate}
                disabled={migrating}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50"
                data-testid="run-migration-btn"
              >
                {migrating ? <Loader size={12} className="animate-spin" /> : <Download size={12} />}
                {migrating ? 'Importing…' : status.invoices_imported > 0 ? 'Re-import from Xero' : 'Import from Xero'}
              </button>
              <button
                onClick={onConnect}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-200 text-blue-700 text-xs font-semibold rounded-md hover:bg-blue-50"
                data-testid="xero-reconnect-btn"
                title="Re-authorize Xero to grant any newly-required permission scopes (e.g. accounting.reports.read for live reconciliation)"
              >
                Reconnect <ExternalLink size={11} />
              </button>
              <button
                onClick={onDisconnect}
                className="px-3 py-1.5 border border-slate-200 text-xs text-slate-500 rounded-md hover:bg-slate-50"
                data-testid="xero-disconnect-btn"
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              onClick={onConnect}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700"
              data-testid="xero-connect-btn"
            >
              Connect to Xero <ExternalLink size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Migration progress ────────────────────────────────────────────────────────
function MigrationProgress({ progress }) {
  if (!progress || progress.status === 'idle') return null;

  const isRunning = progress.status === 'running';
  const isDone    = progress.status === 'done';
  const isError   = progress.status === 'error';

  return (
    <div className={`border rounded-lg p-4 ${
      isRunning ? 'bg-blue-50 border-blue-200'
      : isDone  ? 'bg-emerald-50 border-emerald-200'
      : 'bg-red-50 border-red-200'
    }`} data-testid="migration-progress">
      <div className="flex items-center gap-2 mb-2">
        {isRunning && <Loader size={14} className="animate-spin text-blue-600" />}
        {isDone    && <CheckCircle size={14} className="text-emerald-600" />}
        {isError   && <XCircle    size={14} className="text-red-600" />}
        <p className={`text-sm font-semibold ${
          isRunning ? 'text-blue-800' : isDone ? 'text-emerald-800' : 'text-red-800'
        }`}>
          {isRunning ? progress.step : isDone ? 'Migration complete' : 'Migration error'}
        </p>
      </div>
      {isDone && progress.counts && (
        <div className="flex gap-4 mt-1">
          {Object.entries(progress.counts).map(([k, v]) => (
            <div key={k} className="text-xs text-emerald-700">
              <span className="font-bold">{v.toLocaleString()}</span> {k}
            </div>
          ))}
        </div>
      )}
      {isError && (
        <p className="text-xs text-red-700 mt-1 font-mono">{progress.error}</p>
      )}
    </div>
  );
}

// ── Invoice List ──────────────────────────────────────────────────────────────
function InvoiceList({ hasInvoices }) {
  const [invoices, setInvoices]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [pages, setPages]         = useState(1);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [debSearch, setDebSearch] = useState('');
  const [statusFilter, setStatus] = useState('AUTHORISED');
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    if (!hasInvoices) return;
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 50 });
    if (statusFilter) p.set('status', statusFilter);
    if (debSearch)    p.set('search', debSearch);

    api.get(`/api/xero/invoices?${p}`)
      .then(r => {
        setInvoices(r.data.invoices);
        setTotal(r.data.total);
        setPages(r.data.pages);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [hasInvoices, page, statusFilter, debSearch]);

  useEffect(() => { load(); }, [load]);

  if (!hasInvoices) return null;

  const totalOwed = invoices.reduce((s, i) => s + (i.amount_due || 0), 0);

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="xero-invoice-list">
      {/* Toolbar */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-800">Xero Invoices</p>
          <p className="text-xs text-slate-400">{total.toLocaleString()} invoices · {fmtMoney(totalOwed)} shown owed</p>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search contact, number…"
            className="pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
            data-testid="invoice-search-input"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
              <X size={11} />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="invoice-status-filter"
        >
          <option value="">All statuses</option>
          <option value="AUTHORISED">Awaiting Payment</option>
          <option value="PAID">Paid</option>
          <option value="DRAFT">Draft</option>
          <option value="VOIDED">Voided</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="px-4 py-2.5 text-left text-slate-500 font-medium">Invoice #</th>
              <th className="px-4 py-2.5 text-left text-slate-500 font-medium">Contact</th>
              <th className="px-4 py-2.5 text-left text-slate-500 font-medium">Date</th>
              <th className="px-4 py-2.5 text-left text-slate-500 font-medium">Due</th>
              <th className="px-4 py-2.5 text-right text-slate-500 font-medium">Total</th>
              <th className="px-4 py-2.5 text-right text-slate-500 font-medium">Owed</th>
              <th className="px-4 py-2.5 text-left text-slate-500 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              [...Array(8)].map((_, i) => (
                <tr key={i}>
                  {[...Array(7)].map((_, j) => (
                    <td key={j} className="px-4 py-2.5">
                      <div className="h-3 bg-slate-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                  No invoices found
                </td>
              </tr>
            ) : (
              invoices.map(inv => (
                <tr key={inv.xero_invoice_id}
                  className={`hover:bg-slate-50 ${inv.is_overdue ? 'bg-red-50' : ''}`}
                  data-testid={`invoice-row-${inv.xero_invoice_id}`}
                >
                  <td className="px-4 py-2.5 font-mono text-blue-700">{inv.invoice_number || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-800 font-medium max-w-[180px] truncate">{inv.contact_name}</td>
                  <td className="px-4 py-2.5 text-slate-500">{fmtDate(inv.invoice_date)}</td>
                  <td className="px-4 py-2.5">
                    {inv.is_overdue ? (
                      <span className="text-red-600 font-semibold flex items-center gap-1">
                        <Clock size={10} />{fmtDate(inv.due_date)} ({inv.days_overdue}d)
                      </span>
                    ) : (
                      <span className="text-slate-500">{fmtDate(inv.due_date)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-700">{fmtMoney(inv.total)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">{fmtMoney(inv.amount_due)}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={inv.is_overdue ? 'OVERDUE' : inv.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-400">Page {page} of {pages}</span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="px-2 py-1 text-xs border rounded disabled:opacity-30 hover:bg-slate-50">Prev</button>
            <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}
              className="px-2 py-1 text-xs border rounded disabled:opacity-30 hover:bg-slate-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Fetch Xero Reports ────────────────────────────────────────────────────────
function FetchReportsSection({ onDone }) {
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);

  const fetch = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post('/api/xero/fetch-reports');
      setResult({ ok: true, fetched: r.data.fetched });
      onDone();
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="fetch-reports-section">
      <div className="flex items-start justify-between">
        <div className="flex gap-3">
          <FileText size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-800">Pull Exact P&amp;L &amp; Bank Balances from Xero</p>
            <p className="text-xs text-blue-600 mt-0.5">
              Fetches the Profit &amp; Loss and Bank Summary reports directly from Xero so the
              dashboard matches exactly. Run this once after reconnecting with the
              <code className="bg-blue-100 px-1 rounded">accounting.reports.read</code> scope.
            </p>
          </div>
        </div>
        <button
          onClick={fetch}
          disabled={loading}
          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap ml-4"
          data-testid="fetch-reports-btn"
        >
          {loading ? 'Fetching…' : 'Fetch Reports'}
        </button>
      </div>
      {result && (
        <div className={`mt-3 p-2 rounded text-xs ${result.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-100 text-red-700'}`}
          data-testid="fetch-reports-result">
          {result.ok ? `Cached: ${result.fetched.join(', ')}` : `Error: ${result.error}`}
        </div>
      )}
    </div>
  );
}

// ── Post-Import Setup ─────────────────────────────────────────────────────────
function PostImportSetup() {
  const [obLoading, setObLoading]   = useState(false);
  const [amLoading, setAmLoading]   = useState(false);
  const [obResult,  setObResult]    = useState(null);
  const [amResult,  setAmResult]    = useState(null);

  const computeOB = async () => {
    setObLoading(true); setObResult(null);
    try {
      const r = await api.post('/api/accounts/compute-opening-balances');
      setObResult({ ok: true, n: r.data.accounts_processed });
    } catch (e) {
      setObResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally { setObLoading(false); }
  };

  const markActive = async () => {
    setAmLoading(true); setAmResult(null);
    try {
      const r = await api.post('/api/suppliers/auto-mark-active');
      setAmResult({ ok: true, ...r.data });
    } catch (e) {
      setAmResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally { setAmLoading(false); }
  };

  return (
    <div className="space-y-3">
      {/* Opening Balances */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-semibold text-emerald-800">Compute Opening Balances</p>
            <p className="text-xs text-emerald-700 mt-0.5">
              Automatically calculates and inserts an opening balance entry for any bank account
              that shows a negative running total, so all accounts start from zero.
            </p>
          </div>
          <button onClick={computeOB} disabled={obLoading}
            className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-50 ml-4 whitespace-nowrap"
            data-testid="compute-opening-balances-btn">
            {obLoading ? 'Computing…' : 'Compute Opening Balances'}
          </button>
        </div>
        {obResult && (
          <div className={`mt-2 p-2 rounded text-xs ${obResult.ok ? 'bg-white text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {obResult.ok ? `Done — processed ${obResult.n} bank account(s)` : `Error: ${obResult.error}`}
          </div>
        )}
      </div>

      {/* Auto-mark contacts active */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-semibold text-violet-800">Mark Contacts Active / Inactive</p>
            <p className="text-xs text-violet-700 mt-0.5">
              Scans all contacts and marks anyone with a transaction in the last 2 years as
              Active, and anyone without recent dealings as Inactive.
            </p>
          </div>
          <button onClick={markActive} disabled={amLoading}
            className="px-3 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-md hover:bg-violet-700 disabled:opacity-50 ml-4 whitespace-nowrap"
            data-testid="auto-mark-active-btn">
            {amLoading ? 'Scanning…' : 'Auto-mark Active'}
          </button>
        </div>
        {amResult && (
          <div className={`mt-2 p-2 rounded text-xs ${amResult.ok ? 'bg-white text-violet-700' : 'bg-red-100 text-red-700'}`}>
            {amResult.ok ? `Done — ${amResult.active} active, ${amResult.inactive} inactive (${amResult.total} total)` : `Error: ${amResult.error}`}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Reset All Data ─────────────────────────────────────────────────────────
function ResetDataSection({ onReset }) {
  const [open, setOpen]         = useState(false);
  const [confirm, setConfirm]   = useState('');
  const [resetting, setRes]     = useState(false);
  const [result, setResult]     = useState(null);

  const handleReset = async () => {
    setRes(true);
    setResult(null);
    try {
      const r = await api.post('/api/xero/reset-all-data');
      setResult({ ok: true, summary: r.data.summary });
      setOpen(false);
      setConfirm('');
      onReset();
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setRes(false);
    }
  };

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="reset-data-section">
      <div className="flex items-start justify-between">
        <div className="flex gap-3">
          <Trash2 size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">Clear All Data Before Import</p>
            <p className="text-xs text-red-600 mt-0.5">
              Deletes all transactions, contacts, journal entries and imported Xero data so you can
              start fresh. CRM data (clients, leads, quotes) and user accounts are preserved.
            </p>
          </div>
        </div>
        <button
          onClick={() => setOpen(o => !o)}
          className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-md hover:bg-red-700 whitespace-nowrap ml-4"
          data-testid="open-reset-btn"
        >
          {open ? 'Cancel' : 'Clear All Data'}
        </button>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-red-200">
          <p className="text-xs text-red-700 font-semibold mb-2">
            This will permanently delete:
          </p>
          <ul className="text-xs text-red-600 list-disc ml-4 space-y-0.5 mb-4">
            <li>All journal entries &amp; accounting lines (USA &amp; India)</li>
            <li>All bank transactions</li>
            <li>All Xero imported data (invoices, payments, credit notes, bank txns, journals)</li>
            <li>All contacts</li>
            <li>All internal invoices &amp; invoice lines</li>
          </ul>
          <p className="text-xs text-red-700 mb-2">
            Type <strong>DELETE</strong> to confirm:
          </p>
          <div className="flex gap-2">
            <input
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="flex-1 px-3 py-1.5 text-xs border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="reset-confirm-input"
            />
            <button
              onClick={handleReset}
              disabled={confirm !== 'DELETE' || resetting}
              className="px-4 py-1.5 bg-red-700 text-white text-xs font-bold rounded-md hover:bg-red-800 disabled:opacity-40"
              data-testid="confirm-reset-btn"
            >
              {resetting ? 'Deleting…' : 'Delete Everything'}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className={`mt-3 p-2 rounded text-xs ${result.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-100 text-red-700'}`}
          data-testid="reset-result">
          {result.ok
            ? <>Data cleared. {Object.entries(result.summary).map(([k, v]) => `${k}: ${v}`).join(' · ')}</>
            : `Error: ${result.error}`}
        </div>
      )}
    </div>
  );
}


export default function XeroMigration() {
  const [status, setStatus]           = useState(null);
  const [progress, setProgress]       = useState(null);
  const [migrating, setMigrating]     = useState(false);
  const pollRef                       = useRef(null);

  const loadStatus = useCallback(() => {
    api.get('/api/xero/status').then(r => {
      setStatus(r.data);
      setProgress(r.data.migration);
    }).catch(console.error);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Poll progress while running
  useEffect(() => {
    if (migrating || progress?.status === 'running') {
      pollRef.current = setInterval(() => {
        api.get('/api/xero/migrate/progress').then(r => {
          setProgress(r.data);
          if (r.data.status !== 'running') {
            setMigrating(false);
            clearInterval(pollRef.current);
            loadStatus(); // refresh counts
          }
        }).catch(() => clearInterval(pollRef.current));
      }, 1500);
    }
    return () => clearInterval(pollRef.current);
  }, [migrating, progress?.status, loadStatus]);

  const handleConnect = async () => {
    try {
      const r = await api.get('/api/xero/authorize');
      window.location.href = r.data.authorization_url;
    } catch (e) {
      console.error(e);
    }
  };

  const handleMigrate = async () => {
    setMigrating(true);
    setProgress({ status: 'running', step: 'Starting…', counts: {} });
    try {
      await api.post('/api/xero/migrate');
    } catch (e) {
      setMigrating(false);
      setProgress({ status: 'error', error: e.message });
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect from Xero? Imported data will remain.')) return;
    await api.delete('/api/xero/disconnect');
    loadStatus();
  };

  const handleReset = () => {
    loadStatus();
  };

  const hasInvoices = (status?.counts?.invoices || 0) > 0;

  return (
    <div className="p-6 max-w-5xl" style={{ backgroundColor: '#f8fafc', minHeight: '100%' }} data-testid="xero-migration-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Xero Migration
        </h1>
        <p className="text-xs text-slate-400 mt-1">
          One-time import of all invoices, contacts and payments from Xero into this system.
          Once complete, you can close your Xero account.
        </p>
      </div>

      <div className="space-y-4">

        {/* Connection */}
        <ConnectionCard
          status={status}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          onMigrate={handleMigrate}
          migrating={migrating}
        />

        {/* Migration progress */}
        <MigrationProgress progress={progress} />

        {/* Summary cards after migration */}
        {hasInvoices && status && (
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Invoices imported', value: status.invoices_imported?.toLocaleString(), icon: FileText, color: 'text-blue-600 bg-blue-50' },
              { label: 'Contacts in system', value: (status.counts?.contacts_updated || 0).toLocaleString(), icon: Users, color: 'text-violet-600 bg-violet-50' },
              { label: 'Data source', value: 'Live from Xero', icon: Building2, color: 'text-emerald-600 bg-emerald-50' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-md flex items-center justify-center ${s.color}`}>
                  <s.icon size={15} />
                </div>
                <div>
                  <p className="text-xs text-slate-400">{s.label}</p>
                  <p className="text-sm font-bold text-slate-800">{s.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Invoice list */}
        <InvoiceList hasInvoices={hasInvoices} />

        {/* Fetch Xero Reports */}
        <FetchReportsSection onDone={loadStatus} />

        {/* Post-import: opening balances + auto-mark active */}
        <PostImportSetup />

        {/* Reset all data */}
        <ResetDataSection onReset={handleReset} />

        {/* Help */}
        {!status?.connected && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">How to connect</p>
                <ol className="text-xs text-amber-700 mt-1 space-y-1 list-decimal ml-4">
                  <li>Click "Connect to Xero" above</li>
                  <li>Log in with your Xero credentials</li>
                  <li>Authorise access to your organisation</li>
                  <li>You'll be redirected back here automatically</li>
                  <li>Click "Import from Xero" to pull all invoices and contacts</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
